import { useTranslation } from 'next-i18next'
import React, { CSSProperties, MouseEventHandler, useState } from 'react'
import styled, { DefaultTheme } from 'styled-components'

import { QA } from '@hozana/tests/constants'

import { BorderedDiv } from 'elements/layout/BorderedDiv'
import { Cell } from 'elements/layout/Cell'
import { Div } from 'elements/layout/Div'
import { Li } from 'elements/layout/Li'
import { Row } from 'elements/layout/Row'
import { ScrollDiv } from 'elements/layout/ScrollDiv'
import { StretchCell } from 'elements/layout/StretchCell'
import { Ul } from 'elements/layout/Ul'
import { Icon } from 'elements/media/Icon'
import { Ellipsis } from 'elements/text/Ellipsis'
import { Link } from 'elements/text/Link'
import { Text } from 'elements/text/Text'
import { GradientMaskDiv } from 'elements/ui/GradientMaskDiv'

import { ICON } from 'config/icons'
import theme from 'config/theme'
import { PAGE } from 'routes/constants'

import type { TGuide } from 'modules/guide/types'

const StyledIcon = styled(Icon)`
  background-color: ${(props) => props.theme.colors['black10']};
  border-radius: 3px;
  cursor: pointer;
`

type TStyledBorderedDivProps = {
  hoverBg: keyof DefaultTheme['colors']
  hoverColor: keyof DefaultTheme['colors']
}

const StyledBorderedDiv = styled(BorderedDiv)<TStyledBorderedDivProps>`
  transition: all 200ms ease-in-out;
  &:hover {
    background: ${(props) => props.theme.colors[props.hoverBg]};
    border-color: ${(props) => props.theme.colors[props.hoverBg]};
  }
  &:hover ${Ellipsis} {
    color: ${(props) => props.theme.colors[props.hoverColor]} !important;
  }
`

const getLinkTo = (guideName: string, guide: TGuide) => ({
  pathname: PAGE.GUIDE,
  query: { guideName, subPathParts: guide.path.split('/').slice(2) },
})

const getLi = (
  guideName: string,
  index: React.Key,
  guide: TGuide,
  isRoot: boolean,
  isParent: boolean,
  isGuide: boolean,
  onClick: MouseEventHandler<HTMLAnchorElement>,
  style: CSSProperties = {},
) => (
  <Li key={index} p="6px 0" m="0 0 0 10px" style={style}>
    <Link
      to={getLinkTo(guideName, guide)}
      bold={isParent || isGuide}
      style={{ fontSize: isRoot ? '1.4em' : '1em', lineHeight: '1em', fontFamily: theme.fontFamily['openSans'] }}
      onClick={onClick}
      color="darkBlue"
      hoverColor="blue"
      underline={isGuide}
    >
      {isRoot && <Icon name={ICON.HOME} size="1.4em" m="0 5px 5px 0" />}
      {guide.title}
    </Link>
  </Li>
)

const displayList = (
  guideName: string,
  guide: TGuide,
  parentIndex: number,
  onClick: MouseEventHandler<HTMLAnchorElement>,
) => {
  const getLiMapCallback = (guideListItem: TGuide, index: number) =>
    getLi(guideName, index, guideListItem, false, false, false, onClick)

  if (parentIndex < 0) {
    // Guide itself
    return (
      <>
        {getLi(guideName, 'guide', guide, guide.parents.length === 0, false, true, onClick)}
        {guide.children?.map((guideListItem, index) =>
          getLi(guideName, index, guideListItem, false, false, false, onClick, {
            margin: '0 0 0 40px',
          }),
        )}
        {guide.brothers?.map(getLiMapCallback)}
      </>
    )
  }

  const parentList = displayList(guideName, guide, parentIndex - 1, onClick)

  // Guide parent
  return (
    <>
      {getLi(
        guideName,
        parentIndex,
        guide.parents[parentIndex],
        parentIndex === guide.parents.length - 1,
        true,
        false,
        onClick,
      )}
      {parentIndex === 0 ? (
        // Guide direct parent
        <>
          <Li>
            <Ul m="0 0 0 30px">{parentList}</Ul>
          </Li>
          {guide.uncles?.map(getLiMapCallback)}
        </>
      ) : (
        // Guide ancestor
        <Ul>{parentList}</Ul>
      )}
    </>
  )
}

type TBreadcrumbProps = {
  guideName: string
  guide: TGuide
  parents: TGuide[]
}

export const Breadcrumb: TFC<TBreadcrumbProps> = ({ guideName, guide, parents }) => {
  const [isExpanded, setExpanded] = useState(false)
  const ellipsisLength = {
    xs: `calc(300px - ${parents.length * 30}px)`,
    lg: `calc(${90 / (parents.length + 1)}% - 10px)`,
  }
  const { t } = useTranslation()

  return (
    <Div
      as="nav"
      role="navigation"
      aria-label={t('common:words.breadcrumbs')}
      p={{ xs: '20px 10px 40px 0', lg: '20px 50px 80px' }}
      maxW="1200px"
      m="auto"
    >
      <Row nowrap>
        <StretchCell flex="1">
          <ScrollDiv w="100%" overflowX="auto" overflowY="hidden" p={{ xs: '0 100px 0 1em', lg: '0 5px' }}>
            <Row nowrap gutter="5px">
              {/* Flex: none, to prevent break words */}
              <Cell flex="none">
                <Text color="black30">trans:guide:words.you-are-here</Text>
              </Cell>
              {parents?.map((parent, index) => (
                <Cell key={index} maxW={ellipsisLength}>
                  <StyledBorderedDiv
                    borderColor="black10"
                    borderRadius={index === 0 ? '1em 0 0 1em' : '0'}
                    hoverBg="black10"
                    hoverColor="black50"
                    p="1px 8px"
                    h="25px"
                  >
                    {parent.path ? (
                      <Link to={getLinkTo(guideName, parent)} onClick={() => setExpanded(false)}>
                        <Ellipsis m="0 5px" color="black30">
                          {index === 0 && <Icon name={ICON.HOME} size="1em" color="black50" m="0 5px 3px 0" />}
                          {parent.title}
                        </Ellipsis>
                      </Link>
                    ) : (
                      parent.title
                    )}
                  </StyledBorderedDiv>
                </Cell>
              ))}
              <Cell maxW={ellipsisLength}>
                <BorderedDiv
                  key="last"
                  borderColor="black10"
                  borderRadius={parents.length > 0 ? '0 1em 1em 0' : '1em'}
                  p="1px 8px"
                  h="25px"
                >
                  <Ellipsis m="0 5px" fontSize="1em" color="black30">
                    {guide.title}
                  </Ellipsis>
                </BorderedDiv>
              </Cell>
            </Row>
          </ScrollDiv>
        </StretchCell>
        <Cell>
          <GradientMaskDiv color="background" />
        </Cell>
        {/* WARNING: do not delete id, used in SSR rendering */}
        <Cell id="Breadcrumb__button" data-testid={QA.COMMON.BREADCRUMB_BUTTON}>
          <StyledIcon
            name={isExpanded ? ICON.CLOSE : ICON.CHEVRON_DOWN}
            color="black30"
            size={{ xs: '1.7em', md: '2em' }}
            onClick={() => setExpanded(!isExpanded)}
          />
        </Cell>
      </Row>
      {/* WARNING: do not delete id, used in SSR rendering */}
      <Ul
        as="ol"
        style={{
          height: isExpanded ? 'auto' : '0px',
          overflow: !isExpanded ? 'hidden' : 'auto',
          maxWidth: '800px',
          padding: isExpanded ? '20px 0' : '0',
          margin: '0 auto',
        }}
        id="Breadcrumb__content"
        data-testid={QA.COMMON.BREADCRUMB_CONTENT}
      >
        {displayList(guideName, guide, guide.parents.length - 1, () => setExpanded(false))}
      </Ul>
    </Div>
  )
}
